import { FormControl, FormErrorMessage, Flex, Text } from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import { Enterprise } from "../../types/enterprises.types";
import Input from "../inputs/Input";
import { forwardRef, useImperativeHandle } from "react";
import { fPhoneNumber } from "../../lib/format";

type EnterpriseFormProps = {
	value?: Enterprise;
};

export type EnterpriseFormHandler = {
	values: () => Partial<Enterprise>;
	valid: () => Promise<boolean>;
};

const EnterpriseForm = forwardRef<EnterpriseFormHandler, EnterpriseFormProps>(
	({ value }: EnterpriseFormProps, ref) => {
		const methods = useForm();

		useImperativeHandle(ref, () => ({
			valid: () => {
				return methods.trigger();
			},
			values: () => {
				const values = methods.getValues();
				return {
					name: values.name,
					sageId: values.sageId,
					phoneNumber: values.phoneNumber,
					email: values.email,
					address: {
						streetNumber: values.addressStreetNumber,
						streetName: values.addressStreetName,
						postalCode: values.addressPostalCode,
						city: values.addressCity,
						country: values.addressCountry,
					},
				};
			},
		}));
		return (
			<FormProvider {...methods}>
				<form style={{ marginTop: "32px" }}>
					<Flex direction="column" gap="24px">
						<FormControl isInvalid={!!methods.formState.errors.name}>
							<Input
								id="name"
								placeholder="Nom de l'entreprise"
								defaultValue={value?.name ?? ""}
								{...methods.register("name", {
									required: "Ce champ est obligatoire",
								})}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.name?.message}</>
							</FormErrorMessage>
						</FormControl>
						<FormControl isInvalid={!!methods.formState.errors.sageId}>
							<Input
								id="sageId"
								placeholder="ID sage"
								defaultValue={value?.sageId ?? ""}
								{...methods.register("sageId", {
									required: "Ce champ est obligatoire",
								})}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.sageId?.message}</>
							</FormErrorMessage>
						</FormControl>
						<FormControl isInvalid={!!methods.formState.errors.phoneNumber}>
							<Input
								id="phoneNumber"
								placeholder="Téléphone"
								defaultValue={fPhoneNumber(value?.phoneNumber ?? "")}
								{...methods.register("phoneNumber", {
									required: "Ce champ est obligatoire",
								})}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.phoneNumber?.message}</>
							</FormErrorMessage>
						</FormControl>
						<FormControl isInvalid={!!methods.formState.errors.email}>
							<Input
								id="email"
								placeholder="Email de facturation"
								defaultValue={value?.email ?? ""}
								{...methods.register("email", {
									required: "Ce champ est obligatoire",
								})}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.email?.message}</>
							</FormErrorMessage>
						</FormControl>
						<Text>Adresse de facturation</Text>
						<Flex gap={4}>
							<FormControl
								isInvalid={!!methods.formState.errors.addressStreetNumber}
								width={"10rem"}
							>
								<Input
									id="addressStreetNumber"
									placeholder="Numéro"
									defaultValue={value?.address?.streetNumber ?? ""}
									{...methods.register("addressStreetNumber", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.addressStreetNumber?.message}</>
								</FormErrorMessage>
							</FormControl>
							<FormControl
								isInvalid={!!methods.formState.errors.addressStreetName}
							>
								<Input
									id="addressStreetName"
									placeholder="Adresse (Rue, Avenue, ...)"
									defaultValue={value?.address?.streetName ?? ""}
									{...methods.register("addressStreetName", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.addressStreetName?.message}</>
								</FormErrorMessage>
							</FormControl>
						</Flex>
						<Flex gap={4}>
							<FormControl
								isInvalid={!!methods.formState.errors.addressPostalCode}
								width={"10rem"}
							>
								<Input
									id="addressPostalCode"
									placeholder="Code postal"
									defaultValue={value?.address?.postalCode ?? ""}
									{...methods.register("addressPostalCode", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.addressPostalCode?.message}</>
								</FormErrorMessage>
							</FormControl>

							<FormControl isInvalid={!!methods.formState.errors.addressCity}>
								<Input
									id="addressCity"
									placeholder="Ville"
									defaultValue={value?.address?.city ?? ""}
									{...methods.register("addressCity", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.addressCity?.message}</>
								</FormErrorMessage>
							</FormControl>
						</Flex>
						<FormControl isInvalid={!!methods.formState.errors.addressCountry}>
							<Input
								id="addressCountry"
								placeholder="Pays"
								defaultValue={value?.address?.country ?? ""}
								{...methods.register("addressCountry", {
									required: "Ce champ est obligatoire",
								})}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.addressCountry?.message}</>
							</FormErrorMessage>
						</FormControl>
					</Flex>
				</form>
			</FormProvider>
		);
	},
);

export default EnterpriseForm;
